import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppShell } from "../../components/Sidebar";
import { LegendDots, StatusBadge } from "../../components/StatusBadge";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { gradeSubjectOptions, subjectLabel } from "../../lib/subjects";
import { Search, BookOpen, Clock, Trophy, Calendar, Headset, Copy, Check } from "lucide-react";

const FORMAT = (d) => d || "—";

export default function StudentDashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("all");

  useEffect(() => {
    api.get(`/student/dashboard?lang=${i18n.resolvedLanguage || "en"}`).then((r) => setData(r.data));
  }, [i18n.resolvedLanguage]);

  if (!data) return <AppShell role="student"><p className="text-slate-500">{t("common.loading")}</p></AppShell>;
  const { student, summary, modules } = data;
  const subjectOptions = gradeSubjectOptions(user?.grade || student.grade);
  const filtered = modules.filter((m) =>
    m.module_name.toLowerCase().includes(search.toLowerCase()) &&
    (subject === "all" || m.subject === subject)
  );

  const today = new Date();
  const todayStr = today.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <AppShell role="student">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
            {t("student.dashboard.welcome", { name: student.first_name })} <span aria-hidden>👋</span>
          </h1>
          <p className="mt-1 text-slate-500">{t("student.dashboard.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-2.5 shadow-card">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600"><Calendar size={18} /></div>
          <div>
            <p className="text-sm font-semibold text-slate-900">{t("common.today")}, {todayStr}</p>
            <p className="text-xs text-slate-500">{today.toLocaleDateString("en-GB", { weekday: "long" })}</p>
          </div>
        </div>
      </div>

      {/* VR Login Code — used to sign into the VR headset */}
      {student.vr_code && <VRLoginCodeCard code={student.vr_code} />}

      {/* Profile + summary */}
      <div className="mt-7 rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
        <div className="grid items-center gap-5 lg:grid-cols-[2fr_1fr_1fr_1fr]">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 text-2xl font-bold text-indigo-700">
              {student.first_name[0]}{student.last_name[0]}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{student.full_name}
                <StatusBadge value="Active" className="ml-2 align-middle" withDot />
              </h2>
              <p className="mt-1 text-sm text-slate-500">{student.class_label}</p>
              <p className="text-sm text-slate-500">{student.school_name}</p>
              <p className="text-xs text-slate-400">{t("student.dashboard.last_active")}: {student.last_active}</p>
            </div>
          </div>
          <SummaryStat icon={BookOpen} color="bg-violet-100 text-violet-600" label={t("student.dashboard.modules_progress")} value={`${summary.modules_completed} / ${summary.total_modules}`} sub={t("student.dashboard.completed")} progress={summary.progress_percent} />
          <SummaryStat icon={Clock} color="bg-blue-100 text-blue-600" label={t("student.dashboard.total_time_spent")} value={student.total_time_spent} sub={t("student.dashboard.across_all_modules")} />
          <SummaryStat icon={Trophy} color="bg-emerald-100 text-emerald-600" label={t("student.dashboard.avg_quiz_score")} value={`${summary.average_quiz_score}%`} sub={t("student.dashboard.across_completed_modules")} />
        </div>
      </div>

      {/* Quiz Score Guide */}
      <div className="mt-5 flex flex-wrap items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 text-sm text-slate-600 shadow-card">
        <span className="font-semibold text-slate-700">{t("student.dashboard.quiz_score_guide")}</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {t("student.dashboard.guide_solid")}</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> {t("student.dashboard.guide_moderate")}</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-rose-500" /> {t("student.dashboard.guide_weak")}</span>
      </div>

      {/* Learning progress table */}
      <div className="mt-7 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t("student.dashboard.learning_progress")}</h3>
            <p className="text-sm text-slate-500">{t("student.dashboard.learning_progress_sub")}</p>
          </div>
          <LegendDots />
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="student-search-modules"
              placeholder={t("student.dashboard.search_modules")}
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="student-filter-subject"
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            <option value="all">{t("student.dashboard.all_subjects", { defaultValue: "All Subjects" })}</option>
            {subjectOptions.map((s) => <option key={s} value={s}>{subjectLabel(s)}</option>)}
          </select>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[1000px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th rowSpan={2} className="py-3 pr-4 align-bottom">#</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">{t("student.dashboard.table.module_name")}</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">{t("student.dashboard.table.status")}</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">{t("student.dashboard.table.questions_attempted")}</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">{t("student.dashboard.table.quiz_score")}</th>
                <th colSpan={3} className="pb-1 text-center font-semibold text-slate-700">{t("student.dashboard.table.understanding_level")}</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">{t("student.dashboard.table.last_accessed")}</th>
              </tr>
              <tr className="border-b border-slate-100 text-xs font-semibold text-slate-500">
                <th className="py-2 pr-4">{t("types.Conceptual")}</th>
                <th className="py-2 pr-4">{t("types.Applied")}</th>
                <th className="py-2 pr-4">{t("types.Theoretical")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => (
                <tr key={m.module_id} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="py-4 pr-4 text-slate-500">{i + 1}</td>
                  <td className="py-4 pr-4 font-medium text-slate-900">{m.module_name}</td>
                  <td className="py-4 pr-4"><StatusBadge value={m.status} /></td>
                  <td className="py-4 pr-4 text-slate-700">{m.questions_attempted || 0}</td>
                  <td className="py-4 pr-4 font-semibold text-slate-700">{m.quiz_score != null ? `${m.quiz_score}%` : "—"}</td>
                  <td className="py-4 pr-4"><StatusBadge value={m.conceptual} withDot compact /></td>
                  <td className="py-4 pr-4"><StatusBadge value={m.applied} withDot compact /></td>
                  <td className="py-4 pr-4"><StatusBadge value={m.theoretical} withDot compact /></td>
                  <td className="py-4 pr-4 text-slate-600">{FORMAT(m.last_accessed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-center text-xs text-slate-500">{t("student.dashboard.consistency_tip")}</p>
      </div>
    </AppShell>
  );
}

const SummaryStat = ({ icon: Icon, color, label, value, sub, progress }) => (
  <div>
    <div className="flex items-start gap-3">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}><Icon size={20} /></div>
      <div className="flex-1">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="mt-0.5 text-xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{sub}</p>
        {progress != null && (
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-indigo-600" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    </div>
  </div>
);


const VRLoginCodeCard = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const digits = String(code).padStart(6, "0").split("");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(code));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard unavailable */ }
  };
  return (
    <div
      className="mt-7 rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-600 to-violet-600 p-5 text-white shadow-card"
      data-testid="vr-login-code-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
            <Headset size={20} />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/80">VR Login Code</p>
            <p className="text-sm text-white/85">Use this 6-digit code to sign in to your VR headset.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5" data-testid="vr-login-code-digits">
            {digits.map((d, i) => (
              <span
                key={i}
                className="flex h-11 w-9 items-center justify-center rounded-lg bg-white/95 text-lg font-extrabold tracking-widest text-indigo-700 shadow-sm sm:h-12 sm:w-10 sm:text-xl"
              >{d}</span>
            ))}
          </div>
          <button
            type="button"
            onClick={copy}
            data-testid="vr-login-code-copy-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white/15 px-3 py-2 text-xs font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/25"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
    </div>
  );
};
